import { InteractionInput, Interaction, InteractionsMap, InteractionMode } from "@/components/interactions/types";
import { MaterialNames } from "@/components/material_symbol_names";
import { toFormat, toUTCFormat } from "@/lib/dateHelpers";
import { CRMInteraction } from "@/pages/_layouts/jobs/types";
import { truncate } from "lodash";

export function getIcon(mode: InteractionMode): MaterialNames {
  switch (mode) {
    case InteractionMode.PHONE:
      return "call";
    case InteractionMode.VIDEO:
      return "video_camera_front";
    case InteractionMode.EMAIL:
      return "mail";
    case InteractionMode.IN_PERSON:
      return "record_voice_over";
    case InteractionMode.SMS:
      return "stream_apps";
    case InteractionMode.LETTER:
      return "stylus_note";
    case InteractionMode.LIVE_CHAT:
      return "chat";
    default:
      return "record_voice_over";
  }
}

export function formatInteractionDisplayDate(date: string | Date | null | undefined) {
  if (!date) return null;

  return toUTCFormat(date instanceof Date ? date.toISOString() : date);
}

// Group messages by month/year
export const groupMessages = (messages: any[], referenceDate: Date) => {
  const grouped: Record<string, any[]> = {};
  const referenceDateStr = referenceDate.toLocaleString("en-US", {
    month: "long",
    year: "numeric"
  });

  if (!messages?.length) {
    return {
      [referenceDateStr]: []
    };
  }

  messages.forEach((message) => {
    const date = new Date(message.createdAt);
    const monthYear = date.toLocaleString("en-US", {
      month: "long",
      year: "numeric"
    });

    if (!grouped[monthYear]) {
      grouped[monthYear] = [];
    }

    grouped[monthYear].push(message);
  });

  return grouped;
};

// Helper for amplitude tracking
export function getInteractionTrackingData(
  variables: InteractionInput,
  interaction: Interaction,
  participants: any[],
  formattedSummary: string,
  interactionId: string,
  pgaId: string
) {
  const audience = participants.length > 1 ? "group" : "single";
  const currentMode = interaction?.mode || null;
  const variablesMode = variables.mode || currentMode;
  const messageContent = interaction?.lastMessage?.content;
  const currentActivityType = interaction?.categories?.[0] || null;
  const variablesActivityType = variables.activityType || currentActivityType;

  // Format dates to YYYY-MM-DD
  const formatDate = (date: string | Date | null) => {
    if (!date) return null;
    return new Date(date).toISOString().split("T")[0];
  };

  const currentDate = formatDate(interaction?.interactionDate);
  const newDate = formatDate(variables.interactionDate as string);

  const jobPostId = variables.jobPostId === "" ? null : variables.jobPostId || interaction?.jobPost?.id || null;

  return {
    // Original values are set to null if they match updated values
    originalMode: variablesMode === currentMode ? null : currentMode,
    originalDescription: !variables.description || messageContent === variables.description ? null : messageContent,
    originalActivityType: variablesActivityType === currentActivityType ? null : currentActivityType,
    originalSummary: interaction?.summary,
    originalPrivacyRestricted:
      interaction?.lastMessage?.isPrivate === variables.privacy_restricted ? null : interaction?.lastMessage?.isPrivate,
    originalInteractionDate: currentDate === newDate ? null : currentDate,
    audience,
    mode: variablesMode,
    description: variables.description,
    interactionDate: variables.interactionDate,
    activityType: variables.activityType || currentActivityType,
    summary: formattedSummary,
    privacyRestricted: variables.privacy_restricted || interaction?.lastMessage?.isPrivate,
    interactionId,
    pgaId,
    jobPostId
  };
}

export const getLinkDescription = (mode: InteractionMode, usedAsTitle: boolean = false): string => {
  switch (mode) {
    case InteractionMode.PHONE:
      return usedAsTitle ? "Phone call" : "had a phone call";
    case InteractionMode.VIDEO:
      return usedAsTitle ? "Video conferenced" : "video conferenced";
    case InteractionMode.EMAIL:
      return usedAsTitle ? "Emailed" : "emailed";
    case InteractionMode.IN_PERSON:
      return usedAsTitle ? "Met in-person" : "met in-person";
    case InteractionMode.SMS:
      return usedAsTitle ? "Texted" : "texted";
    case InteractionMode.LETTER:
      return usedAsTitle ? "Corresponded" : "corresponded";
    case InteractionMode.LIVE_CHAT:
      return usedAsTitle ? "Live chatted" : "live chatted";
    default:
      return usedAsTitle ? "Interacted" : "interacted";
  }
};

export function getInteractionDisplayName(
  interaction: {
    owner?: { name: string } | null;
    mode?: InteractionMode | null;
    relatedTo?: string | null;
  },
  withName?: string | null
): string {
  const { relatedTo, mode, owner } = interaction;
  const resolvedWith = withName ?? relatedTo;
  const withPerson = resolvedWith ? ` with ${resolvedWith}` : "";

  if (owner?.name && mode) {
    return `${owner.name} ${getLinkDescription(mode)}${withPerson}`;
  }
  if (mode) {
    return `${getLinkDescription(mode, true)}${withPerson}`;
  }
  return owner?.name || resolvedWith || "Interaction";
}

/** Max participant names shown under a "Group of X" heading before "+N more". */
export const MAX_VISIBLE_INTERACTION_PARTICIPANT_NAMES = 5;

type InteractionParticipantNameSource = {
  firstName?: string | null;
  lastName?: string | null;
};

function formatParticipantFullName(participant: InteractionParticipantNameSource): string {
  return [participant.firstName, participant.lastName].filter(Boolean).join(" ").trim();
}

/**
 * Side-sheet / list heading for interaction participants.
 * Single person → full name; multiple → "Group of N"; empty → relatedTo fallback.
 */
export function getInteractionParticipantHeading(
  participants: InteractionParticipantNameSource[] | null | undefined,
  relatedTo?: string | null
): string {
  if (!participants?.length) {
    const fallback = relatedTo?.trim();
    return fallback || "Interaction";
  }

  if (participants.length === 1) {
    return formatParticipantFullName(participants[0]) || relatedTo?.trim() || "Interaction";
  }

  return `Group of ${participants.length}`;
}

/**
 * Limited comma-separated participant names for group interactions.
 * Returns undefined for single-participant or empty lists.
 */
export function getInteractionParticipantNamesLabel(
  participants: InteractionParticipantNameSource[] | null | undefined,
  maxVisible: number = MAX_VISIBLE_INTERACTION_PARTICIPANT_NAMES
): string | undefined {
  if (!participants || participants.length <= 1) {
    return undefined;
  }

  const names = participants.map(formatParticipantFullName).filter(Boolean);
  if (names.length === 0) {
    return undefined;
  }

  if (names.length <= maxVisible) {
    return names.join(", ");
  }

  const visible = names.slice(0, maxVisible);
  const remaining = names.length - maxVisible;
  return `${visible.join(", ")} +${remaining} more`;
}

export const getLinkDescriptionForJobInteractions = (mode: InteractionMode): string => {
  switch (mode) {
    case InteractionMode.PHONE:
    case InteractionMode.VIDEO:
    case InteractionMode.SMS:
    case InteractionMode.LIVE_CHAT:
      return "had a";
    case InteractionMode.EMAIL:
      return "messaged via";
    case InteractionMode.IN_PERSON:
      return "met";
    case InteractionMode.LETTER:
      return "sent a";
    default:
      return "had a";
  }
};

export const filterInteractionsForSummary = (interactions: Interaction[], depthInMonths: number = 6) => {
  // Early exit conditions
  if (!interactions?.length) return [];

  const accessibleInteractions = filterAndSortInteractions(interactions, depthInMonths);
  if (accessibleInteractions.length <= 2) return [];

  const groupedInteractions = groupByTwoDayPeriods(accessibleInteractions);
  const processedInteractions = processGroups(groupedInteractions);

  return transformInteractions(processedInteractions.slice(0, 5));
};
// Helper functions

//  calculate the date for the depth in months
const filterAndSortInteractions = (interactions: Interaction[], depthInMonths: number) => {
  const depthInMonthsAgo = new Date();
  depthInMonthsAgo.setMonth(depthInMonthsAgo.getMonth() - depthInMonths);

  const seenIds = new Set<string>();
  return interactions
    .filter((interaction) => {
      const interactionDate = new Date(interaction.interactionDate);
      if (!interaction.lastMessage.haveAccess || interactionDate < depthInMonthsAgo || seenIds.has(interaction.id)) {
        return false;
      }
      seenIds.add(interaction.id);
      return true;
    })
    .sort((a, b) => new Date(b.interactionDate).getTime() - new Date(a.interactionDate).getTime());
};

const getTwoDayPeriod = (date: Date): string => {
  const startDay = Math.floor((date.getDate() - 1) / 2) * 2 + 1;
  return new Date(date.getFullYear(), date.getMonth(), startDay).toISOString();
};

const groupByTwoDayPeriods = (interactions: Interaction[]) => {
  return interactions.reduce(
    (acc, interaction) => {
      const bucketDate = getTwoDayPeriod(new Date(interaction.interactionDate));
      if (!acc[bucketDate]) acc[bucketDate] = [];
      acc[bucketDate].push(interaction);
      return acc;
    },
    {} as Record<string, Interaction[]>
  );
};

const transformInteractions = (interactions: Interaction[]) => {
  return interactions.map((interaction: Interaction) => ({
    id: interaction.id,
    date: new Date(interaction.interactionDate).toISOString(),
    mode: interaction.mode as string,
    description: interaction.summary || "",
    participants: interaction.participants.map((p) => `${p.firstName} ${p.lastName}`),
    summary: interaction.summary || "",
    comments: interaction.lastMessage?.content || "",
    topics: interaction.categories || []
  }));
};

export const handleSummary = (interaction: Interaction | CRMInteraction) => {
  const { summary, lastMessage } = interaction;
  const text = summary || lastMessage.content || "";
  return truncate(text, { length: 600 });
};

const processGroups = (groupedInteractions: Record<string, Interaction[]>) => {
  const processedIds = new Set<string>();
  const processedInteractions: Interaction[] = [];

  Object.values(groupedInteractions).forEach((periodInteractions) => {
    const uniquePeriodInteractions = periodInteractions.filter((interaction) => {
      if (processedIds.has(interaction.id)) return false;
      processedIds.add(interaction.id);
      return true;
    });

    if (uniquePeriodInteractions.length >= 5) {
      // For periods with 5+ interactions, only take the most recent one
      processedInteractions.push(uniquePeriodInteractions[0]);
    } else {
      // For periods with fewer interactions, include all
      processedInteractions.push(...uniquePeriodInteractions);
    }
  });

  return processedInteractions.sort(
    (a, b) => new Date(b.interactionDate).getTime() - new Date(a.interactionDate).getTime()
  );
};

export const groupInteractions = (interactions: Interaction[]): InteractionsMap => {
  // Step 1: Group by month/year
  const interactionsMap = interactions.reduce((acc: InteractionsMap, interaction) => {
    const interactionDate = new Date(interaction.interactionDate);
    const key = toFormat(interactionDate.toISOString().split("T")[0], "MMMM yyyy") || "";
    if (!acc[key]) acc[key] = [];
    acc[key].push(interaction);
    return acc;
  }, {});

  // Step 2: Sort the month-year keys DESC
  const sortedMonths = Object.keys(interactionsMap).sort((a, b) => {
    const [monthA, yearA] = a.split(" ");
    const [monthB, yearB] = b.split(" ");

    // Order by year DESC
    if (yearA !== yearB) {
      return parseInt(yearB) - parseInt(yearA);
    }

    // Order by month DESC
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ];
    return months.indexOf(monthB) - months.indexOf(monthA);
  });

  // Step 3: Order interactions within each month by date DESC
  const result: InteractionsMap = {};
  sortedMonths.forEach((month) => {
    const sortedInteractions = interactionsMap[month].sort((a, b) => {
      return new Date(b.interactionDate).getTime() - new Date(a.interactionDate).getTime();
    });

    result[month] = sortedInteractions;
  });

  return result;
};
